import React from 'react';
import { observer } from 'mobx-react-lite';
import mapCurrency from 'currency-symbol-map';
import { RenderAsHTMLStyles, RotateLoader, Tooltip } from '@src/features/common';
import { Briefcase } from 'styled-icons/fa-solid/Briefcase';
import Tags from '@src/assets/svg/employerSidebar/tags.inline.svg';
import { getLearning } from '@src/services/board';
import styled from 'styled-components';
import { themeGet } from 'styled-system';
import { useClipboardWithState } from '@src/features/preferences';
import { Modal, Flex, TextBox, StyledButton, BaseLink, Delimiter, BaseBox, Column } from 'src/kit';

type Props = {
  learningId: number;
  onClose: () => void;
};

export const LearningInfoModal = observer(({ learningId, onClose }: Props) => {
  const [state, setState] = React.useState<{ learning: any; isLoading: boolean }>({
    isLoading: false,
    learning: {}
  });
  const [clipboard, copyToClipboard] = useClipboardWithState();

  React.useEffect(() => {
    async function fetchData() {
      setState(prevState => ({ ...prevState, isLoading: true }));
      const learning = await getLearning(learningId);
      setState({ isLoading: false, learning });
    }
    fetchData();
  }, [learningId]);

  const { isLoading, learning } = state;

  if (isLoading) {
    return <RotateLoader height="100%">Loading...</RotateLoader>;
  }

  const currencySymbol = mapCurrency(learning.currency);
  const GBPIcon = mapCurrency('GBP');

  const learningLink = () => {
    if (learning.is_talent_sift) {
      if (learning.university_resource_url) return { link: learning.university_resource_url, label: 'Get access' };
      if (learning.link) return { link: learning.link, label: 'Access' };
    } else {
      if (learning.link) return { link: learning.link };
    }
    if (learning.pdf_view_url) return { link: learning.pdf_view_url, label: 'View' };
    if (learning.questions?.length || learning.videos?.length) return { link: learning.item_url, label: 'View more' };
    return {};
  };

  const { link, label } = learningLink();

  return (
    <Modal borderRadius="4px" p="40px 40px 50px 40px" onClose={onClose} boxShadow="shadow1" color="textSecondary">
      <Flex height={20} mb={30} alignItems="center" justifyContent="space-between">
        <TextBox textStyle="caps" height={16} color="textSecondary" letterSpacing="1.14px" fontSize={16}>
          {learning.provider?.title}
        </TextBox>
      </Flex>
      <TextBox mb={20} textStyle="ellipsis" color="textPrimary" fontSize={20} fontWeight="bold">
        {learning.title}
      </TextBox>
      {learning.matching_categories && learning.matching_subcategories && (
        <Column>
          {learning.matching_categories.length > 0 && (
            <Flex mb={learning.matching_subcategories.length > 0 ? 0 : 30}>
              <BaseBox mr={5} as={Tags} size={14} />
              <TextBox color="textSecondary" fontSize="14px">
                {learning.matching_categories.map(({ name }) => name).join(', ')}
              </TextBox>
            </Flex>
          )}
          {learning.matching_subcategories.length > 0 && (
            <Flex mt={15} mb={30}>
              <BaseBox mr={5} as={Briefcase} size={14} color="textSecondary" />
              {learning.matching_subcategories.map(({ name }) => name).join(', ')}
            </Flex>
          )}
        </Column>
      )}

      <Delimiter mb={30} />
      {learning.text && (
        <Field
          flexWrap="wrap"
          textStyle="firstLetter"
          options={{ wrap: true, capitalize: false }}
          value={<ModalContent dangerouslySetInnerHTML={{ __html: learning.text }}></ModalContent>}
        />
      )}
      <Flex justifyContent="flex-end">
        {link && (
          <>
            <Tooltip open={clipboard.copied} title="Copied">
              <StyledButton
                onClick={() => {
                  copyToClipboard(learning.item_url);
                }}
                variant="ghost"
                size="md"
                width="120px"
                lineHeight="1"
              >
                Share
              </StyledButton>
            </Tooltip>

            <StyledButton ml={20} as={BaseLink} variant="caInverted" size="md" color="white" href={link} openInNewTab>
              {!learning.free && learning.link && (
                <>
                  {`Get this deal: `}

                  {learning.price && (
                    <>
                      {learning.currency !== 'GBP' && <>{`${GBPIcon}${Number(learning.gbp_price).toLocaleString()}`}</>}
                    </>
                  )}
                  {learning.price ? ` (${currencySymbol}${Number(learning.price).toLocaleString()})` : ''}
                  {learning.promo_code ? ` Promo code: ${learning.promo_code}` : ''}
                </>
              )}
              {label}
            </StyledButton>
          </>
        )}
      </Flex>
    </Modal>
  );
});

const Label = props => <TextBox mr={20} color="textPrimary" fontSize={16} {...{ minWidth: '140px' }} {...props} />;
const Value = props => (
  <TextBox color="textSecondary" fontWeight="normal" fontSize={16} textStyle="capitalize" {...props} />
);
const Field = ({ label, value, textStyle, ...rest }: any) =>
  value ? (
    <Flex mb={30} {...rest}>
      <Label>{label}</Label>
      <Value textStyle={textStyle}>{value}</Value>
    </Flex>
  ) : null;

const ModalContent = styled(RenderAsHTMLStyles)`
  line-height: 1.5rem;

  a {
    color: ${themeGet('colors.primary')};
    cursor: pointer;
    text-decoration: none;

    &.disabled {
      pointer-events: none;
    }
  }
`;
